import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import {
  AssignmentModalProps,
  TrailerAssignmentData,
} from "../AssignmentModal";
import enumLabel from "../../../../utils/labels/enumLabel";

import {
  GetReceiverListWithStorageFacilitiesQuery,
  GoodProfile,
  TrailerCompartmentAssignment,
} from "../../../../graphql/generated";
import { HTMLAttributes, useEffect, useMemo, useState } from "react";
import { capitalize, keyBy, sum } from "lodash";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import goodUnitLabel from "../../../../utils/labels/goodUnitsLabel";
import { testIds } from "./TrailerCompartmentAssignmentForm.testIds";
import { buildProductTypeByCommodity } from "../compartmentValidation/productTypeByCommodity";
import {
  validateAllTrailersPlacement,
  CompartmentViolation,
} from "../compartmentValidation/validateCompartmentProductPlacement";
import { validateAssignedQuantities } from "../compartmentValidation/validateAssignedQuantities";

import CompartmentGraphic, {
  CompartmentVolume,
  maxFill,
} from "../../../common/CompartmentGraphic";

function violationKey(v: CompartmentViolation): string {
  switch (v.type) {
    case "DOUBLE_BULKHEAD":
      return `${v.type}-${v.compartmentId}-${v.nextCompartmentId}`;
    case "UNTYPED":
    case "OVER_ASSIGNED":
      return `${v.type}-${v.commodityId}`;
    default:
      return `${v.type}-${v.compartmentId}`;
  }
}

function violationMessage(
  t: (key: string, fallback: string, opts?: Record<string, unknown>) => string,
  v: CompartmentViolation,
  commodityLabel: (commodityId: string) => string
): string {
  switch (v.type) {
    case "HEAVY_ONLY":
      return t("compartmentValidation.heavyOnly", "{{compartment}}: a heavy product must be loaded into a Heavy Only compartment", { compartment: v.compartmentId });
    case "DOUBLE_BULKHEAD":
      return t("compartmentValidation.doubleBulkhead", "{{a}} / {{b}}: a double bulkhead is required between a heavy and a light product", { a: v.compartmentId, b: v.nextCompartmentId });
    case "MAX_VOLUME":
      return t("compartmentValidation.maxVolume", "{{compartment}}: {{volume}} exceeds the maximum volume of {{max}}", { compartment: v.compartmentId, volume: v.volume, max: v.max });
    case "MIN_VOLUME":
      return t("compartmentValidation.minVolume", "{{compartment}}: {{volume}} is below the minimum volume of {{min}}", { compartment: v.compartmentId, volume: v.volume, min: v.min });
    case "MIXED_COMPARTMENT":
      return t("compartmentValidation.mixedCompartment", "{{compartment}}: a compartment cannot hold a heavy and a light product at the same time", { compartment: v.compartmentId });
    case "UNTYPED":
      return t("compartmentValidation.untyped", "{{commodity}}: set a Product Type (Heavy or Light) on this commodity before loading it next to another product", { commodity: v.commodityId });
    case "OVER_ASSIGNED":
      return t("compartmentValidation.overAssigned", "{{commodity}}: {{assigned}} assigned is more than the {{ordered}} ordered", { commodity: commodityLabel(v.commodityId), assigned: v.assigned, ordered: v.ordered });
  }
}

export type TrailerCompartmentAssignmentFormProps = {
  trailer: TrailerAssignmentData;
  additionalTrailers: TrailerAssignmentData[];
  shipments: AssignmentModalProps["shipments"];
  trip: AssignmentModalProps["trip"];
  goodProfiles: GoodProfile[];
  receivers: GetReceiverListWithStorageFacilitiesQuery["businessEntities"]["data"];
  compartmentAssignments: TrailerCompartmentAssignment[];
  onCompartmentAssignmentsChange: (
    compartmentAssignments: TrailerCompartmentAssignment[]
  ) => void;
  onValidationChange?: (violations: CompartmentViolation[]) => void;
  /** Volume tracking layer — passed from the container when trackCommodity is enabled */
  compartmentVolumes?: CompartmentVolume[];
  onEditVolume?: (compartmentId: string, trailerId: string) => void;
  /** Suppresses the per-compartment metadata row in the volume layer */
  hideVolumeMetadata?: boolean;
};

const TrailerCompartmentAssignmentForm: React.FC<TrailerCompartmentAssignmentFormProps> =
  ({
    compartmentAssignments,
    onCompartmentAssignmentsChange,
    onValidationChange,
    trailer,
    additionalTrailers,
    shipments,
    trip,
    receivers,
    goodProfiles,
    compartmentVolumes,
    onEditVolume,
    hideVolumeMetadata,
  }) => {
    const { t } = useTranslation(["common", "assets", "trailers"]);
    const { enqueueSnackbar } = useSnackbar();
    const [selectedCompartment, setSelectedCompartment] =
      useState<
        | (NonNullable<typeof trailer["compartments"]>[0] & {
            trailerId: string;
          })
        | null
      >(null);

    const selectedCompartmentAssignment =
      compartmentAssignments.find(
        (compartmentAssignment) =>
          compartmentAssignment.compartmentId ===
            selectedCompartment?.identifier &&
          (!compartmentAssignment.trailerId ||
            compartmentAssignment.trailerId === selectedCompartment?.trailerId)
      ) || null;

    // Keeps what the user typed, so the field can be emptied while editing.
    const [volumeInput, setVolumeInput] = useState("");

    const selectedAssignmentKey = selectedCompartmentAssignment
      ? [
          selectedCompartmentAssignment.trailerId || "",
          selectedCompartmentAssignment.compartmentId,
          selectedCompartmentAssignment.commodityId,
        ].join("-")
      : "";

    // Refill the field when another compartment or commodity is picked, not while typing.
    useEffect(() => {
      setVolumeInput(
        selectedCompartmentAssignment?.quantity
          ? String(selectedCompartmentAssignment.quantity)
          : ""
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAssignmentKey]);

    const allShippedGoods = useMemo(
      () =>
        (shipments
          ? shipments.map((shipment) => shipment.shipmentLocations)
          : trip
          ? trip.shipmentLocations
          : []
        )
          .flat()
          .map((location) => location.shippedGoods || [])
          .flat(),
      [shipments, trip]
    );

    const allShippedGoodsById = keyBy(allShippedGoods, (good) => good._id);

    const allReceivedGoods = useMemo(
      () =>
        (shipments
          ? shipments.flatMap((shipment) => shipment.shipmentLocations)
          : trip
          ? trip.shipmentLocations
          : []
        ).flatMap((location) => location.receivedGoods || []),
      [shipments, trip]
    );

    const commoditySelectValue = useMemo(() => {
      if (!selectedCompartmentAssignment?.commodityId) {
        return "";
      }

      const matchingReceivedGood = allReceivedGoods.find(
        (receivedGood) =>
          receivedGood.goodProfileId ===
            selectedCompartmentAssignment.commodityId &&
          receivedGood.storageFacilityId ===
            selectedCompartmentAssignment.targetStorageFacilityId
      );

      if (!matchingReceivedGood) {
        return "";
      }

      return JSON.stringify({
        goodProfileId: matchingReceivedGood.goodProfileId,
        quantity: matchingReceivedGood.quantity,
        storageFacilityId: matchingReceivedGood.storageFacilityId,
      });
    }, [selectedCompartmentAssignment, allReceivedGoods]);

    const remainingGoodQuantites = allShippedGoods.map((good) => {
      const goodProfile = goodProfiles.find(
        (goodProfile) => goodProfile._id === good.goodProfileId
      );
      const goodQuantity = sum(
        compartmentAssignments
          .filter(
            (compartmentAssignment) =>
              compartmentAssignment.commodityId === good._id ||
              compartmentAssignment.commodityId === good.goodProfileId
          )
          .map((compartmentAssignment) => compartmentAssignment.quantity)
      );
      return {
        good,
        goodProfile,
        remainingQuantity: good.quantity - goodQuantity,
      };
    });

    // Quantity of a received good already placed across compartments. When
    // excludeSelected is set, the selected compartment is skipped so re-picking a
    // commodity for it does not count its own placement twice.
    const assignedQuantityForGood = (
      receivedGood: {
        goodProfileId?: string | null;
        storageFacilityId?: string | null;
      },
      excludeSelected = false
    ) =>
      sum(
        compartmentAssignments
          .filter(
            (a) =>
              a.commodityId === receivedGood.goodProfileId &&
              a.targetStorageFacilityId === receivedGood.storageFacilityId &&
              !(
                excludeSelected &&
                a.compartmentId === selectedCompartment?.identifier &&
                (!a.trailerId ||
                  a.trailerId === selectedCompartment?.trailerId)
              )
          )
          .map((a) => a.quantity)
      );

    const commodityLabel = (commodityId: string) =>
      allShippedGoods.find(
        (good) => good._id === commodityId || good.goodProfileId === commodityId
      )?.label || commodityId;

    const allCompartments = trailer?.compartments
      ?.map((compartment) => ({
        ...compartment,
        trailerId: trailer._id,
      }))
      ?.concat(
        additionalTrailers.flatMap((additionalTrailer) =>
          (additionalTrailer.compartments || []).map((compartment) => ({
            ...compartment,
            trailerId: additionalTrailer._id,
          }))
        )
      );

    const compartmentViolations = useMemo(() => {
      const toCompartmentInput = (t: TrailerAssignmentData) => ({
        _id: t._id,
        compartments: (t.compartments || []).map((compartment) => ({
          identifier: compartment.identifier,
          capacity: compartment.capacity,
          minVolume: compartment.minVolume,
          maxVolume: compartment.maxVolume,
          heavyOnly: compartment.heavyOnly,
          hasDoubleBulkheadAfter: compartment.hasDoubleBulkheadAfter,
        })),
      });

      const productTypeByCommodity = buildProductTypeByCommodity(
        [...allShippedGoods, ...allReceivedGoods],
        goodProfiles
      );

      // Validate the main trailer AND every additional trailer — an invalid
      // placement on an additional trailer must also block the assignment.
      const placementViolations = validateAllTrailersPlacement({
        mainTrailer: trailer ? toCompartmentInput(trailer) : null,
        additionalTrailers: additionalTrailers.map(toCompartmentInput),
        assignments: compartmentAssignments.map((a) => ({
          trailerId: a.trailerId,
          compartmentId: a.compartmentId,
          commodityId: a.commodityId,
          quantity: a.quantity,
        })),
        productTypeByCommodity,
      });

      // Volumes can be overridden, so also check nothing is loaded past what
      // was ordered.
      const quantityViolations = validateAssignedQuantities({
        orderedGoods: allShippedGoods,
        assignments: compartmentAssignments.map((a) => ({
          commodityId: a.commodityId,
          quantity: a.quantity,
        })),
      });

      return [...placementViolations, ...quantityViolations];
    }, [
      trailer,
      additionalTrailers,
      compartmentAssignments,
      allShippedGoods,
      allReceivedGoods,
      goodProfiles,
    ]);

    useEffect(() => {
      onValidationChange?.(compartmentViolations);
    }, [compartmentViolations, onValidationChange]);

    const updateSelectedCompartmentAssignment = (
      update: Partial<TrailerCompartmentAssignment>
    ) => {
      if (selectedCompartmentAssignment) {
        onCompartmentAssignmentsChange(
          compartmentAssignments.map((compartmentAssignment) => {
            if (
              compartmentAssignment.compartmentId ===
                selectedCompartmentAssignment.compartmentId &&
              (!compartmentAssignment.trailerId ||
                compartmentAssignment.trailerId ===
                  selectedCompartmentAssignment.trailerId)
            ) {
              return {
                ...compartmentAssignment,
                ...update,
              };
            } else {
              return compartmentAssignment;
            }
          })
        );
      } else {
        onCompartmentAssignmentsChange([
          ...compartmentAssignments,
          {
            ...update,
            compartmentId: selectedCompartment?.identifier || "",
            commodityId: update.commodityId || "",
            quantity: update.quantity || 0,
            targetStorageFacilityId: update.targetStorageFacilityId || "",
            trailerId: selectedCompartment?.trailerId || null,
          },
        ]);
      }
    };

    const canHandleSelectedTrailer = (currentIndex: number) => {
      if (currentIndex > 0 && compartmentAssignments.length < currentIndex) {
        return false;
      }
      return true;
    };

    const allTrailers = [trailer, ...(additionalTrailers || [])];

    return (
      <Stack spacing={2}>
        {remainingGoodQuantites
          .filter(
            (remainingGoodQuantity) =>
              remainingGoodQuantity.remainingQuantity > 0
          )
          .map((remainingGoodQuantity) => (
            <Box key={remainingGoodQuantity.good._id}>
              <Alert severity="warning">
                {remainingGoodQuantity.goodProfile?.label} -{" "}
                {remainingGoodQuantity.remainingQuantity}{" "}
                {enumLabel(remainingGoodQuantity.goodProfile?.unit)} remaining
                to assign
              </Alert>
            </Box>
          ))}
        {compartmentViolations.map((violation) => (
          <Box key={violationKey(violation)}>
            <Alert severity="error">
              {violationMessage(t, violation, commodityLabel)}
            </Alert>
          </Box>
        ))}
        <CompartmentGraphic
          compartments={(allCompartments || []).map((c) => ({
            identifier: c.identifier,
            capacity: c.capacity,
            unit: c.unit,
            hasDoubleBulkheadAfter: c.hasDoubleBulkheadAfter,
            trailerId: c.trailerId,
            maxVolume: c.maxVolume,
          }))}
          compartmentAssignments={compartmentAssignments}
          availableGoods={allShippedGoods.map((g) => ({
            _id: g._id,
            label: g.label,
            goodProfileId: g.goodProfileId,
            color:
              goodProfiles.find((gp) => gp._id === g.goodProfileId)?.color ??
              null,
          }))}
          selectedCompartmentId={selectedCompartment?.identifier ?? null}
          onCompartmentClick={(compartmentId, trailerId) => {
            const trailerIndex = allTrailers.findIndex(
              (t) => t._id === trailerId
            );
            if (!canHandleSelectedTrailer(trailerIndex)) {
              enqueueSnackbar(
                t(
                  "trailers:cantSelectTrailer",
                  "Previous trailers must have at least one compartment filled"
                ),
                { variant: "warning" }
              );
              return;
            }
            const trailerForId = allTrailers[trailerIndex];
            const compartment = trailerForId?.compartments?.find(
              (c) => c.identifier === compartmentId
            );
            if (compartment) {
              setSelectedCompartment({ ...compartment, trailerId });
            }
          }}
          compartmentVolumes={compartmentVolumes}
          onEditVolume={onEditVolume}
          hideVolumeMetadata={hideVolumeMetadata}
        />

        {/* The automation ID belongs on the visible MUI select trigger. */}
        {/* eslint-disable-next-line local-rules/require-data-testid */}
        <TextField
          label={capitalize(t("assets:commodity.one", "Commodity"))}
          select
          disabled={!selectedCompartment}
          value={commoditySelectValue}
          onChange={(event) => {
            if (!event.target.value) {
              onCompartmentAssignmentsChange(
                compartmentAssignments.filter(
                  (compartmentAssignment) =>
                    !(
                      compartmentAssignment.compartmentId ===
                        selectedCompartment?.identifier &&
                      (!compartmentAssignment.trailerId ||
                        compartmentAssignment.trailerId ===
                          selectedCompartment?.trailerId)
                    )
                )
              );
              return;
            }
            const receivedGood = JSON.parse(
              event.target.value
            ) as typeof allReceivedGoods[0];
            // Fill the compartment: place up to what is left of the good, capped
            // at the compartment size. This lets a good be split across several
            // compartments.
            const remaining =
              (receivedGood.quantity || 0) -
              assignedQuantityForGood(receivedGood, true);
            const quantity = selectedCompartment
              ? Math.max(0, Math.min(remaining, maxFill(selectedCompartment)))
              : Math.max(0, remaining);
            updateSelectedCompartmentAssignment({
              commodityId: receivedGood.goodProfileId,
              quantity,
              targetStorageFacilityId: receivedGood.storageFacilityId,
            });
          }}
          name="commodity"
          SelectProps={{
            SelectDisplayProps: {
              "data-testid":
                testIds.trailerCompartmentAssignmentFormCommodity,
            } as HTMLAttributes<HTMLDivElement>,
          }}
        >
          <MenuItem
            data-testid={testIds.trailerCompartmentAssignmentFormNone}
            value={""}
          >
            None
          </MenuItem>
          {allReceivedGoods.map((receivedGood) => {
            const good = allShippedGoodsById[receivedGood.goodId];
            if (!good) {
              return null;
            }
            const isCurrentSelection =
              selectedCompartmentAssignment?.commodityId ===
                receivedGood.goodProfileId &&
              selectedCompartmentAssignment?.targetStorageFacilityId ===
                receivedGood.storageFacilityId;
            const fullyAssigned =
              assignedQuantityForGood(receivedGood) >=
              (receivedGood.quantity || 0);
            return (
              <MenuItem
                data-testid={
                  testIds.trailerCompartmentAssignmentFormCommodityMenuItem(
                    receivedGood.goodProfileId,
                  )
                }
                key={good._id}
                value={JSON.stringify({
                  goodProfileId: receivedGood.goodProfileId,
                  quantity: receivedGood.quantity,
                  storageFacilityId: receivedGood.storageFacilityId,
                })}
                disabled={fullyAssigned && !isCurrentSelection}
              >
                {good.label} - {receivedGood.quantity}{" "}
                {goodUnitLabel(good.unit)} - {receivedGood.storageFacilityId}
              </MenuItem>
            );
          })}
        </TextField>

        <TextField
          label={capitalize(t("assets:volume", "Volume"))}
          type="number"
          disabled={!selectedCompartmentAssignment}
          value={volumeInput}
          onChange={(event) => {
            const typed = event.target.value;
            setVolumeInput(typed);
            const volume = Number(typed);
            updateSelectedCompartmentAssignment({
              quantity:
                typed !== "" && Number.isFinite(volume) && volume > 0
                  ? volume
                  : 0,
            });
          }}
          name="volume"
          inputProps={
            {
              min: 0,
              "data-testid": testIds.trailerCompartmentAssignmentFormVolume,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
      </Stack>
    );
  };

export default TrailerCompartmentAssignmentForm;
